// «Ayer en 10 segundos» con DATOS REALES: las slides se generan en runtime
// desde brief + hoteles + alertas + premio (lo mismo que ve el resto de la
// app). Sin datos (demo), se generan igual con el mock — siempre coherentes
// con lo que la app enseña, jamás cifras fijas de demo desincronizadas.
import type { Alerta } from '@/constants/data';
import type { AgeingLive, Brief, HotelRow, Premio } from '@/api/client';
import type { Slide } from '@/constants/data';
import { fmt, fmtDec, fmtE, fmtM } from '@/lib/format';

const MESES = ['', 'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const ddmm = (iso?: string) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');

export function construirSlides(
  brief: Brief | null,
  hoteles: HotelRow[],
  alertas: Alerta[],
  premio: Premio | null,
  ageing?: AgeingLive | null,
): Slide[] {
  const out: Slide[] = [];
  const h = new Date().getHours();
  const saludo = h < 13 ? 'Buenos días' : h < 21 ? 'Buenas tardes' : 'Buenas noches';

  out.push({
    colors: ['#0E2B3D', '#128F86'],
    kick: `Ona Pulse${brief?.fecha ? ` · ${ddmm(brief.fecha)}` : ''}`,
    h: saludo,
    sub: 'Esto pasó ayer en tu grupo',
    foot: 'Datos de Hotelos · desliza o espera',
    waves: true,
  });

  if (brief) {
    out.push({
      colors: ['#07222F', '#0B4A4C'],
      kick: `Producción de ayer (${ddmm(brief.fecha)})`,
      h: fmtE(brief.prodDia),
      sub: `${fmtM(brief.prodMes)} en el mes · neto sin IVA`,
      foot: `${brief.hoteles} hoteles reportando en ${brief.pms} PMS`,
    });
  }

  /* 🔴 LA DIAPOSITIVA FINANCIERA, que entra en el sitio de la de *sync*. Acuerdo
     de la demo: «fuera la diapositiva de sync, entra una financiera».
     Se ha elegido LA CARTERA y no otra cosa por descarte razonado: contra
     presupuesto no se puede (`budMes` viaja null hasta que se defina la
     referencia comparable), el F&B del mes en curso es provisional y no cuadra
     con el P&L, y la producción ya tiene su propia diapositiva justo antes. Lo
     que queda —y es lo más financiero que hay— es el dinero que va a entrar: con
     nombre de cliente, importe y fecha, y cuadrado contra Business Central.
     Es además la cifra que hace levantar la cabeza en un comité. */
  const p30 = ageing?.prox30;
  if (p30 && p30.total > 0) {
    out.push({
      colors: ['#07222F', '#134E5E'],
      kick: 'La cartera',
      h: `${fmtM(p30.total)} por cobrar`,
      sub: `${fmt(p30.clientes)} clientes con vencimiento en los próximos 30 días`,
      foot: ageing.vencido > 0
        ? `Y ${fmtM(ageing.vencido)} ya vencido · cartera de Business Central`
        : 'Cartera abierta de Business Central',
    });
  }

  // Solo compiten los que tienen el día volcado: premiar a uno mientras a otro
  // le falta el dato sería premiar al que llegó antes al espejo.
  const top = [...hoteles].filter((h) => h.d != null).sort((a, b) => (b.d ?? 0) - (a.d ?? 0))[0];
  if (top && (top.d ?? 0) > 0) {
    out.push({
      colors: ['#2B2410', '#7A5A16'],
      kick: 'El hotel del día',
      h: top.n.replace(/^Ona /i, ''),
      sub: `${fmtE(top.d ?? 0)} ayer · ${fmt(Math.round(top.mes / 1e3))} k€ en el mes`,
      foot: `${top.occ != null ? `Ocupación hoy ${fmtDec(top.occ)}% · ` : ''}felicita al equipo 👏`,
    });
  }

  if (brief?.occ != null) {
    const llenos = hoteles.filter((x) => x.occ != null && x.occ >= 95).length;
    out.push({
      colors: ['#07222F', '#0E3A5C'],
      kick: 'Esta noche',
      h: `${fmtDec(brief.occ)}% de ocupación`,
      sub: llenos ? `${llenos} hotel(es) por encima del 95%` : 'el grupo, en una cifra',
      foot: brief.adr != null ? `ADR del mes: ${fmtDec(brief.adr)} € · rejilla 4 PMS` : 'rejilla 4 PMS',
    });
  }

  /* 🔴 FUERA LAS ALERTAS DE SISTEMAS. Aquí estaba la «diapositiva de sync» que
     Patricio quería quitar, y no era una diapositiva propia: era esta, que coge
     la PRIMERA alerta — y las alertas incluyen sincronizaciones caídas. Al CEO le
     salía «Atención: sincronización caída», que es un asunto de Sistemas y no de
     su resumen del día. Se filtran por departamento: lo de negocio (overbooking,
     cobros) se queda; el fontanero, a la pestaña de Alertas. */
  const deNegocio = alertas.filter((a) => (a.dep ?? '') !== 'sis');
  const ob = deNegocio.find((a) => a.ic === 'bed');
  if (ob) {
    out.push({
      colors: ['#331A16', '#7A2E2A'],
      kick: 'Atención',
      h: ob.t.replace('Overbooking en ', 'Overbooking: '),
      sub: ob.detalle?.[0] ? `${ob.detalle[0][0]} · ${ob.detalle[0][1]} de ocupación` : ob.m,
      foot: deNegocio.length > 1 ? `Y ${deNegocio.length - 1} alerta(s) más en la pestaña Alertas` : 'Detalle en la pestaña Alertas',
    });
  } else if (deNegocio.length) {
    out.push({
      colors: ['#331A16', '#7A2E2A'],
      kick: 'Atención',
      h: deNegocio[0].t,
      sub: deNegocio[0].m,
      foot: deNegocio.length > 1 ? `Y ${deNegocio.length - 1} alerta(s) más` : '',
    });
  }

  if (premio?.disponible && premio.podio?.length) {
    const oro = premio.podio[0];
    out.push({
      colors: ['#2B2410', '#8A6A16'],
      kick: `Premios Noa · ${MESES[premio.mes ?? 0]}`,
      h: oro.n.replace(/^Ona /i, ''),
      sub: oro.detalle ?? (oro.valor != null ? `${fmtDec(oro.valor)} · edición publicada` : 'edición publicada'),
      foot: 'El podio completo, en la portada de Dirección',
      tortugona: true,
    });
  }

  out.push({
    colors: ['#0E2B3D', '#0F6E6A'],
    kick: 'Hasta mañana',
    h: 'Que tengas buen día',
    sub: brief?.fecha ? `Mañana a las 8:00, el ${ddmm(brief.fechaNatural ?? brief.fecha)}` : 'Mañana a las 8:00, más',
    foot: 'Ona Pulse · el latido del grupo',
    waves: true,
  });

  return out;
}
